import { useState } from 'react';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';

// ── EmailJS config ──────────────────────────────────────────
// Steps to activate real email sending:
// 1. Go to https://www.emailjs.com and create a FREE account
// 2. Add Email Service (Gmail) → copy Service ID
// 3. Create Email Template → copy Template ID
// 4. Get your Public Key from Account > API Keys
// 5. Replace the 3 values below
const EMAILJS_SERVICE_ID  = 'service_2n2ve0g';
const EMAILJS_TEMPLATE_ID = 'template_vwiyjkk';
const EMAILJS_PUBLIC_KEY  = 'kYpSxUDBTnclm-UVq';
// ────────────────────────────────────────────────────────────

const SERVICES_LIST = ['Web Development','Web Design','App Development','SEO','Digital Marketing','Other'];
const BUDGETS = [
  'Under ₹25K ($300 | €260)',
  '₹25K–₹75K ($300–900 | €260–780)',
  '₹75K–₹2L ($900–2.4K | €780–2.1K)',
  '₹2L+ ($2.4K+ | €2.1K+)'
];

export default function Contact() {
  useScrollReveal();
  const [form, setForm] = useState({ name:'', email:'', phone:'', company:'', service:'', budget:'', message:'' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required.';
    if (!form.message.trim()) e.message = 'Please describe your project.';
    return e;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setStatus('sending');

    const templateParams = {
      from_name:    form.name,
      from_email:   form.email,
      phone:        form.phone || 'Not provided',
      company:      form.company || 'Not provided',
      service:      form.service || 'Not specified',
      budget:       form.budget || 'Not specified',
      message:      form.message,
      to_email:     'chaturbhujarani78@gmail.com',
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setStatus('success');
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  const inputClass = field =>
    `w-full bg-white dark:bg-[#111] border rounded-xl px-4 py-3 text-sm text-black dark:text-white placeholder:text-black/25 dark:text-white/25 focus:outline-none focus:border-black dark:border-white transition-colors ${errors[field] ? 'border-red-400' : 'border-black/15 dark:border-white/15'}`;

  return (
    <>
      <SEO title="Contact Us — Get a Free Quote" description="Get in touch with CodeViora for web development, web design, app development, SEO, and digital marketing. Free consultation within 24 hours." canonical="/contact" />

      {/* Hero */}
      <section className="bg-grid-black pt-24 pb-16 md:pt-32 md:pb-20 relative overflow-hidden">
        <div className="container-max relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <span className="section-label-inv">Contact Us</span>
              <h1 className="section-title-inv text-5xl md:text-6xl mt-2">LET'S BUILD SOMETHING GREAT.</h1>
              <p className="section-sub-inv">Tell us about your project. We respond within 24 hours with a free consultation — no obligation, no sales pressure.</p>
            </div>
            <div className="hidden lg:block animate-fade-in" style={{animationDelay:'0.2s'}}>
              <img src={require('../images/b1.webp')} alt="Contact CodeViora team" className="rounded-3xl img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-grid-white py-16 md:py-20">
        <div className="container-max">
          <div className="grid lg:grid-cols-5 gap-16 items-start">

            {/* Sidebar */}
            <div className="lg:col-span-2 flex flex-col gap-6 reveal">
              <div className="card-white p-7">
                <h2 className="mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500,fontSize:'1.1rem'}}>REACH US DIRECTLY</h2>
                <div className="flex flex-col gap-5">
                  {[
                    { icon:'✉️', label:'Email', value:'chaturbhujarani78@gmail.com' },
                    { icon:'📞', label:'Phone', value:'+91 86993 93272' },
                    { icon:'📍', label:'Location', value:'India (Remote Worldwide)' },
                    { icon:'⏰', label:'Response Time', value:'Within 24 hours' },
                  ].map(({ icon, label, value }) => (
                    <div key={label} className="flex items-start gap-4">
                      <span className="text-xl flex-shrink-0">{icon}</span>
                      <div>
                        <p className="text-xs text-black/30 dark:text-white/30 mb-0.5">{label}</p>
                        <p className="text-sm text-black dark:text-white font-medium break-all">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card-white p-7">
                <h2 className="mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500,fontSize:'1.1rem'}}>WHAT HAPPENS NEXT</h2>
                <ol className="flex flex-col gap-4">
                  {[
                    'We review your message and project details.',
                    'We schedule a free 30-min consultation call.',
                    'We send a detailed proposal with timeline & pricing.',
                    'We kick off your project.',
                  ].map((step, i) => (
                    <li key={step} className="flex items-start gap-3 text-sm text-black/50 dark:text-white/50">
                      <span className="w-6 h-6 rounded-full bg-black text-white font-medium text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="card-white p-7">
                <h2 className="mb-4" style={{fontFamily:'Anton,sans-serif',fontWeight:500,fontSize:'1.1rem'}}>OUR SERVICES</h2>
                <div className="flex flex-wrap gap-2">
                  {SERVICES_LIST.filter(s => s !== 'Other').map(s => (
                    <span key={s} className="text-xs bg-black text-white font-medium px-3 py-1.5 rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3 reveal">
              {status === 'success' ? (
                <div className="card-white p-12 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-black flex items-center justify-center mx-auto mb-6">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-white">
                      <circle cx="14" cy="14" r="12" stroke="currentColor" strokeWidth="1.8"/>
                      <path d="M9 14l3 3 7-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h2 className="text-3xl text-black dark:text-white mb-3" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>MESSAGE SENT!</h2>
                  <p className="text-black/50 dark:text-white/50 text-sm max-w-sm mx-auto mb-6">Thanks for reaching out. We will review your project and get back to you within 24 hours at <strong>chaturbhujarani78@gmail.com</strong>.</p>
                  <button onClick={() => { setStatus('idle'); setForm({ name:'',email:'',phone:'',company:'',service:'',budget:'',message:'' }); }} className="btn-primary">Send Another</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="card-white p-8 flex flex-col gap-5" aria-label="Contact form">
                  <div>
                    <h2 className="text-xl mb-1" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>TELL US ABOUT YOUR PROJECT</h2>
                    <p className="text-xs text-black/40 dark:text-white/40">Your message will be sent to chaturbhujarani78@gmail.com</p>
                  </div>

                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
                      ⚠️ Failed to send. Please configure EmailJS keys or email us directly at chaturbhujarani78@gmail.com
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-black/50 dark:text-white/50 mb-2 uppercase tracking-wide">Your Name <span className="text-red-400">*</span></label>
                      <input id="name" name="name" type="text" value={form.name} onChange={handleChange} placeholder="John Kram" className={inputClass('name')} aria-required="true" />
                      {errors.name && <p className="text-xs text-red-500 mt-1.5" role="alert">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-black/50 dark:text-white/50 mb-2 uppercase tracking-wide">Email <span className="text-red-400">*</span></label>
                      <input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="JohnKram@company.com" className={inputClass('email')} aria-required="true" />
                      {errors.email && <p className="text-xs text-red-500 mt-1.5" role="alert">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-xs font-medium text-black/50 dark:text-white/50 mb-2 uppercase tracking-wide">Phone</label>
                      <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" className={inputClass('phone')} />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-xs font-medium text-black/50 dark:text-white/50 mb-2 uppercase tracking-wide">Company</label>
                      <input id="company" name="company" type="text" value={form.company} onChange={handleChange} placeholder="Your Company" className={inputClass('company')} />
                    </div>
                  </div>

                  <div>
                    <span className="block text-xs font-medium text-black/50 dark:text-white/50 mb-2 uppercase tracking-wide">Service Needed</span>
                    <div className="flex flex-wrap gap-2">
                      {SERVICES_LIST.map(s => (
                        <button key={s} type="button" onClick={() => setForm(f => ({ ...f, service: s }))}
                          className={`text-xs font-medium px-4 py-2 rounded-full border transition-colors ${form.service === s ? 'bg-black border-black dark:border-white text-white' : 'border-black/15 dark:border-white/15 text-black/50 dark:text-white/50 hover:border-black dark:border-white hover:text-black dark:hover:text-white dark:text-white'}`}
                          aria-pressed={form.service === s}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className="block text-xs font-medium text-black/50 dark:text-white/50 mb-2 uppercase tracking-wide">Budget Range</span>
                    <div className="flex flex-wrap gap-2">
                      {BUDGETS.map(b => (
                        <button key={b} type="button" onClick={() => setForm(f => ({ ...f, budget: b }))}
                          className={`text-xs font-medium px-4 py-2 rounded-full border transition-colors ${form.budget === b ? 'bg-black border-black dark:border-white text-white' : 'border-black/15 dark:border-white/15 text-black/50 dark:text-white/50 hover:border-black dark:border-white hover:text-black dark:hover:text-white dark:text-white'}`}
                          aria-pressed={form.budget === b}>
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-black/50 dark:text-white/50 mb-2 uppercase tracking-wide">Project Details <span className="text-red-400">*</span></label>
                    <textarea id="message" name="message" rows={5} value={form.message} onChange={handleChange}
                      placeholder="Tell us what you want to build, what problem you are solving, and any timeline or deadline you have in mind..."
                      className={`${inputClass('message')} resize-none`} aria-required="true" />
                    {errors.message && <p className="text-xs text-red-500 mt-1.5" role="alert">{errors.message}</p>}
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <p className="text-xs text-black/30 dark:text-white/30">Free consultation. No commitment.</p>
                    <button type="submit" disabled={status==='sending'} className="btn-primary disabled:opacity-50">
                      {status === 'sending' ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/></svg>
                          Sending...
                        </span>
                      ) : (
                        <>Send Message <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map / contact visual */}
      <section className="bg-pure-black py-16 border-t border-white/10">
        <div className="container-max">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon:'🚀', title:'Fast Turnaround', desc:'Most projects kick off within 48 hours of agreement.' },
              { icon:'🔒', title:'NDA Available', desc:'We sign NDAs before any sensitive project discussion.' },
              { icon:'🌍', title:'Remote-Friendly', desc:'We work with clients across India and worldwide.' },
            ].map(({ icon, title, desc }, i) => (
              <div key={title} className="reveal card-black p-7 text-center" style={{transitionDelay:`${i*80}ms`}}>
                <span className="text-4xl mb-4 block">{icon}</span>
                <h3 className="text-white mb-2" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                <p className="text-sm text-white/40">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
